/**
 * Unified Permission Utility Functions
 * Centralized permission checking with consistent format
 * 
 * @author Employee Management System
 * @version 3.0.0
 */

// Standard permission format configuration
export const PERMISSION_CONFIG = {
  PREFIX: 'permission.',
  SEPARATOR: '.',
  WILDCARD: '.*',
  ACTIONS: ['create', 'delete', 'edit', 'read', 'update', 'write'],
  MODULES: [
    // Aligned with JWT Modules claim (12 modules)
    'announcements',
    'brands',
    'campaigns',
    'companies', //sadece administor için görünür
    'dashboard',
    'draws',
    'tickets',
    'videos',
    'payments',
    'gifts',
    'reports',
    'subTenants',
    'surveys',
    'users'      //sadece tenant admin için görünür
  ]
};

/**
 * Normalize permission format
 * Removes inconsistencies in permission naming
 * @param {string} permission - Raw permission string
 * @returns {string} Normalized permission
 */
export const normalizePermission = (permission) => {
  if (!permission || typeof permission !== 'string') return '';
  
  // Remove 'permission.' prefix if exists
  let normalized = permission.startsWith(PERMISSION_CONFIG.PREFIX) 
    ? permission.substring(PERMISSION_CONFIG.PREFIX.length)
    : permission;
  
  return normalized.toLowerCase().trim();
};

/**
 * Check if user has a specific permission
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} requiredPermission - Permission to check
 * @returns {boolean} Whether user has the permission
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions) || !requiredPermission) {
    return false;
  }
  
  const normalizedRequired = normalizePermission(requiredPermission);
  const normalizedUserPerms = userPermissions.map(normalizePermission);
  
  // Direct permission match
  if (normalizedUserPerms.includes(normalizedRequired)) {
    return true;
  }
  
  // Wildcard permission check
  const [module] = normalizedRequired.split(PERMISSION_CONFIG.SEPARATOR);
  const wildcardPermission = `${module}${PERMISSION_CONFIG.WILDCARD}`;
  
  if (normalizedUserPerms.includes(wildcardPermission)) {
    return true;
  }
  
  // Check if user has wildcard that covers this permission
  const userWildcards = normalizedUserPerms.filter(perm => perm.endsWith(PERMISSION_CONFIG.WILDCARD));
  
  for (const wildcard of userWildcards) {
    const wildcardModule = wildcard.replace(PERMISSION_CONFIG.WILDCARD, '');
    if (normalizedRequired.startsWith(`${wildcardModule}${PERMISSION_CONFIG.SEPARATOR}`)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Check if user has any of the specified permissions (OR logic)
 * @param {string[]} userPermissions - User's permissions array
 * @param {string[]} requiredPermissions - Permissions to check
 * @returns {boolean} Whether user has any of the permissions
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
    return false;
  }
  
  return requiredPermissions.some(permission => 
    hasPermission(userPermissions, permission)
  );
};

/**
 * Check if user has all specified permissions (AND logic)
 * @param {string[]} userPermissions - User's permissions array
 * @param {string[]} requiredPermissions - Permissions to check
 * @returns {boolean} Whether user has all permissions
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
    return false;
  }
  
  return requiredPermissions.every(permission => 
    hasPermission(userPermissions, permission)
  );
};

/**
 * Check if user can access a module
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} module - Module name
 * @returns {boolean} Whether user can access the module
 */
export const canAccessModule = (userPermissions, module) => {
  if (!module || !userPermissions) return false;
  
  const modulePermissions = PERMISSION_CONFIG.ACTIONS.map(action => `${module}.${action}`);
  modulePermissions.push(`${module}${PERMISSION_CONFIG.WILDCARD}`);
  
  return hasAnyPermission(userPermissions, modulePermissions);
};

/**
 * Check read permission for a resource
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} resource - Resource name
 * @returns {boolean} Whether user can read the resource
 */
export const canRead = (userPermissions, resource) => {
  const readPermissions = [
    `${resource}.read`,
    `${resource}${PERMISSION_CONFIG.WILDCARD}`
  ];
  return hasAnyPermission(userPermissions, readPermissions);
};

/**
 * Check write permission for a resource
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} resource - Resource name
 * @returns {boolean} Whether user can write to the resource
 */
export const canWrite = (userPermissions, resource) => {
  const writePermissions = [
    `${resource}.write`,
    `${resource}.create`,
    `${resource}.edit`,
    `${resource}.update`,
    `${resource}${PERMISSION_CONFIG.WILDCARD}`
  ];
  return hasAnyPermission(userPermissions, writePermissions);
};

/**
 * Check delete permission for a resource
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} resource - Resource name
 * @returns {boolean} Whether user can delete from the resource
 */
export const canDelete = (userPermissions, resource) => {
  const deletePermissions = [
    `${resource}.delete`,
    `${resource}${PERMISSION_CONFIG.WILDCARD}`
  ];
  return hasAnyPermission(userPermissions, deletePermissions);
};

/**
 * Get default permissions by role
 * @param {string} role - User role
 * @returns {string[]} Default permissions for the role
 */
export const getDefaultPermissionsByRole = (role) => {
  const allModulePermissions = PERMISSION_CONFIG.MODULES.flatMap(module => 
    PERMISSION_CONFIG.ACTIONS.map(action => `${module}.${action}`)
  );
  
  switch (role?.toLowerCase()) {
    case 'admin':
    case 'administrator':
      // Admin gets all permissions
      return allModulePermissions;
      
    case 'tenantadmin':
    case 'tenant_admin':
      // TenantAdmin gets comprehensive permissions (not quite everything)
      return PERMISSION_CONFIG.MODULES.flatMap(module => [
        `${module}.read`,
        `${module}.write`,
        ...(module !== 'reports' ? [`${module}.delete`] : []),
        ...(module !== 'payments' ? [`${module}.delete`] : []),
        ...(module !== 'gifts' ? [`${module}.delete`] : []),
        ...(module !== 'companies' ? [`${module}.delete`] : []),
        ...(module !== 'dashboard' ? [`${module}.delete`] : []),
        ...(module !== 'videos' ? [`${module}.delete`] : []),
        ...(module !== 'subTenants' ? [`${module}.delete`] : []),
      ]);
      
    case 'user':
      // User gets no default permissions - must be explicitly assigned
      return [];
      
    default:
      return [];
  }
};

/**
 * Check if user can access the system
 * @param {Object} user - User object with role and permissions
 * @returns {boolean} Whether user can access the system
 */
export const canAccessSystem = (user) => {
  if (!user) return false;
  
  const { role, permissions } = user;
  
  // Admin and TenantAdmin can always access
  if (role === 'admin' || role === 'tenantAdmin') {
    return true;
  }
  
  // User role requires permissions
  if (role === 'user') {
    return permissions && Array.isArray(permissions) && permissions.length > 0;
  }
  
  return false;
};

/**
 * Format permission for display
 * @param {string} permission - Permission string
 * @returns {string} User-friendly permission label
 */
export const formatPermissionLabel = (permission) => {
  if (!permission) return '';
  
  const normalized = normalizePermission(permission);
  const parts = normalized.split(PERMISSION_CONFIG.SEPARATOR);
  
  if (parts.length < 2) return normalized;
  
  const [module, action] = parts;
  
  const moduleNames = {
    announcements: 'Announcements',
    brands: 'Brands',
    campaigns: 'Campaigns',
    companies: 'Companies', //sadece administor için görünür
    dashboard: 'Dashboard',
    draws: 'Draws',
    tickets: 'Tickets',
    videos: 'Videos',
    payments: 'Payments',
    gifts: 'Gifts',
    reports: 'Reports',
    subTenants: 'subTenants',
    surveys: 'Surveys',
    users: 'Users' //sadece tenant admin için görünür
  };
  
  const actionNames = {
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    read: 'View',
    update: 'Update',
    write: 'Edit',
    '*': 'All Actions'
  };
  
  const moduleName = moduleNames[module] || module;
  const actionName = actionNames[action] || action;
  
  return `${moduleName} - ${actionName}`;
};

/**
 * Group permissions by module
 * @param {string[]} permissions - Permissions array
 * @returns {Object} Permissions grouped by module
 */
export const groupPermissionsByModule = (permissions) => {
  if (!permissions || !Array.isArray(permissions)) return {};
  
  const grouped = {};
  
  permissions.forEach(permission => {
    const normalized = normalizePermission(permission);
    const [module, action] = normalized.split(PERMISSION_CONFIG.SEPARATOR);
    
    if (module && action) {
      if (!grouped[module]) {
        grouped[module] = [];
      }
      
      grouped[module].push({
        permission: normalized,
        action,
        label: formatPermissionLabel(permission)
      });
    }
  });
  
  return grouped;
};

/**
 * Validate permission format
 * @param {string} permission - Permission to validate
 * @returns {boolean} Whether permission format is valid
 */
export const isValidPermission = (permission) => {
  if (!permission || typeof permission !== 'string') return false;
  
  const normalized = normalizePermission(permission);
  const parts = normalized.split(PERMISSION_CONFIG.SEPARATOR);
  
  if (parts.length !== 2) return false;
  
  const [module, action] = parts;
  
  // Check if module is recognized
  if (!PERMISSION_CONFIG.MODULES.includes(module)) return false;
  
  // Check if action is recognized or wildcard
  if (action !== '*' && !PERMISSION_CONFIG.ACTIONS.includes(action)) return false;
  
  return true;
};

/**
 * Generate all possible permissions for a module
 * @param {string} module - Module name
 * @returns {string[]} All possible permissions for the module
 */
export const generateModulePermissions = (module) => {
  if (!module || !PERMISSION_CONFIG.MODULES.includes(module)) return [];
  
  return [
    ...PERMISSION_CONFIG.ACTIONS.map(action => `${module}.${action}`),
    `${module}${PERMISSION_CONFIG.WILDCARD}`
  ];
};

// Export all utility functions
const permissionUtils = {
  // Core functions
  normalizePermission,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  
  // Module functions
  canAccessModule,
  canRead,
  canWrite,
  canDelete,
  
  // Role functions
  getDefaultPermissionsByRole,
  canAccessSystem,
  
  // Utility functions
  formatPermissionLabel,
  groupPermissionsByModule,
  isValidPermission,
  generateModulePermissions,
  
  // Configuration
  PERMISSION_CONFIG
};

export default permissionUtils;